import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { RefreshCw, Play, CheckCircle2, AlertTriangle, Clock, HardDrive, Mail, Database } from 'lucide-react';
import MigrationsPanel from '../components/MigrationsPanel';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(2)}s`;
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(1);
  return `${m}m ${s}s`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => {
  if (status === 'success') {
    return <span className="cb-pill cb-pill--positive" data-testid="status-pill"><CheckCircle2 className="w-3 h-3" /> Success</span>;
  }
  if (status === 'failed') {
    return <span className="cb-pill cb-pill--negative" data-testid="status-pill"><AlertTriangle className="w-3 h-3" /> Failed</span>;
  }
  if (status === 'running') {
    return <span className="cb-pill" data-testid="status-pill"><Clock className="w-3 h-3" /> Running</span>;
  }
  return <span className="cb-pill" data-testid="status-pill">{status || '—'}</span>;
};

const ConfigBadge = ({ ok, label }) => (
  <span className={`cb-pill ${ok ? 'cb-pill--positive' : 'cb-pill--negative'}`} data-testid={`config-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
    {ok ? '✓' : '✗'} {label}
  </span>
);

const Settings = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        axiosInstance.get('/admin/backup/status'),
        axiosInstance.get('/admin/backup/logs?limit=15'),
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch backup status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const triggerBackup = async () => {
    if (running) return;
    setRunning(true);
    try {
      await axiosInstance.post('/admin/backup/run');
      toast.success('Backup started. Refreshing status in 10 seconds…');
      // Poll status a few times for instant feedback
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        await fetchAll();
        if (i === 5) toast.info('Auto-refresh complete. Pull to refresh later if status is still "running".');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start backup');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="p-8" data-testid="settings-page-loading">Loading…</div>;
  }

  const cfg = status?.configured || {};
  const last = status?.last_backup;
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/cron`;

  return (
    <div className="p-8" data-testid="settings-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">System</p>
            <h1 className="cb-h1">Settings</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchAll}
              data-testid="refresh-status-button"
              className="cb-btn-secondary"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={triggerBackup}
              disabled={running}
              data-testid="backup-now-button"
              className="cb-btn-primary"
            >
              <Play className="w-4 h-4" />
              {running ? 'Backing up…' : 'Backup Now'}
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* ------- Configuration card ------- */}
      <div className="cb-card mb-6" style={{ padding: 'var(--cb-space-6)' }} data-testid="config-card">
        <h2 className="cb-section-title flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
          <HardDrive className="w-5 h-5" />
          Backup Configuration
        </h2>
        <p className="cb-section-sub">Confirm all four green pills are present before relying on the daily schedule.</p>

        <div className="flex flex-wrap mt-4" style={{ gap: 'var(--cb-space-2)' }}>
          <ConfigBadge ok={cfg.active_auth_method === 'oauth' || cfg.active_auth_method === 'service_account'} label={cfg.active_auth_method === 'oauth' ? 'OAuth2 (primary)' : cfg.active_auth_method === 'service_account' ? 'Service Account (fallback)' : 'No GDrive Auth'} />
          <ConfigBadge ok={cfg.gdrive_folder_id_set} label="GDrive Folder ID" />
          <ConfigBadge ok={cfg.resend_api_key_set} label="Resend API Key" />
          <ConfigBadge ok={!!cfg.resend_from_email} label="Resend From" />
          <ConfigBadge ok={!!cfg.alert_recipient} label="Alert Recipient" />
          <ConfigBadge ok={cfg.webhook_secret_set} label="Webhook Secret" />
        </div>

        <div className="grid grid-cols-2 gap-4 mt-5" style={{ fontSize: '0.875rem' }}>
          <div>
            <p className="cb-card-label">Environment</p>
            <p className="cb-card-value cb-card-value--sm">{cfg.environment_tag}</p>
          </div>
          <div>
            <p className="cb-card-label">Schedule (UTC)</p>
            <p className="cb-card-value cb-card-value--sm cb-num">{cfg.schedule_utc}</p>
          </div>
          <div>
            <p className="cb-card-label">Uploading As</p>
            <p className="cb-num" style={{ fontSize: '0.8125rem', wordBreak: 'break-all' }}>{cfg.principal_identity || cfg.service_account_email || '—'}</p>
          </div>
          <div>
            <p className="cb-card-label">Alert Recipient</p>
            <p style={{ fontSize: '0.8125rem' }}>
              <Mail className="w-3 h-3 inline mr-1" />
              {cfg.alert_recipient || '—'}
            </p>
          </div>
          <div>
            <p className="cb-card-label">Min Size Threshold</p>
            <p className="cb-num cb-card-value--sm">{formatBytes(cfg.min_size_bytes)}</p>
          </div>
          <div>
            <p className="cb-card-label">Daily Retention</p>
            <p className="cb-num cb-card-value--sm">{cfg.daily_retention} backups</p>
          </div>
        </div>

        <div className="cb-notice mt-5" style={{ cursor: 'default' }}>
          <div className="flex-1">
            <p className="cb-notice-sub">
              <strong style={{ color: 'var(--cb-ink)' }}>External cron webhook (safety net):</strong>{' '}
              Point any free cron service (cron-job.org, GitHub Actions) at the URL below at 02:05 UTC daily.
              Include the header <code className="cb-num">X-Backup-Secret: &lt;your secret&gt;</code>.
            </p>
            <p className="cb-num" style={{ fontSize: '0.75rem', marginTop: '6px', wordBreak: 'break-all', color: 'var(--cb-ink)' }} data-testid="webhook-url">
              POST {webhookUrl}
            </p>
          </div>
        </div>
      </div>

      {/* ------- Last backup status card ------- */}
      <div className="cb-card mb-6" style={{ padding: 'var(--cb-space-6)' }} data-testid="last-backup-card">
        <h2 className="cb-section-title flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
          <Database className="w-5 h-5" />
          Last Backup Status
        </h2>

        {!last ? (
          <p className="cb-muted mt-4">No backups have run yet. Click <strong>Backup Now</strong> to create the first one.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <p className="cb-card-label">Status</p>
                <div style={{ marginTop: '6px' }}><StatusPill status={last.status} /></div>
              </div>
              <div>
                <p className="cb-card-label">Started</p>
                <p className="cb-card-value cb-card-value--sm cb-num" style={{ fontSize: '0.9375rem' }} data-testid="last-started">
                  {formatDateTime(last.started_at)}
                </p>
              </div>
              <div>
                <p className="cb-card-label">File Size</p>
                <p className="cb-card-value cb-card-value--sm cb-num" data-testid="last-size">
                  {formatBytes(last.file_size_bytes)}
                </p>
              </div>
              <div>
                <p className="cb-card-label">Duration</p>
                <p className="cb-card-value cb-card-value--sm cb-num" data-testid="last-duration">
                  {formatDuration(last.duration_seconds)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5" style={{ fontSize: '0.875rem' }}>
              <div>
                <p className="cb-card-label">Triggered By</p>
                <p>{last.triggered_by} <span className="cb-muted">({last.source})</span></p>
              </div>
              <div>
                <p className="cb-card-label">Environment</p>
                <p>{last.environment_tag} <span className="cb-muted">/ {last.db_name}</span></p>
              </div>
              <div>
                <p className="cb-card-label">Documents</p>
                <p className="cb-num">{last.total_documents?.toLocaleString() || 0}</p>
              </div>
              <div>
                <p className="cb-card-label">Mongo Host Hash</p>
                <p className="cb-num" style={{ fontSize: '0.75rem' }}>{last.mongo_host_hash}</p>
              </div>
            </div>

            {last.validation && (
              <div className="mt-5" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
                <p className="cb-card-label" style={{ marginBottom: 'var(--cb-space-2)' }}>Validation Report</p>
                <div className="flex flex-wrap" style={{ gap: 'var(--cb-space-2)', fontSize: '0.8125rem' }}>
                  <ConfigBadge ok={last.validation.zip_opens} label="ZIP opens" />
                  <ConfigBadge ok={last.validation.manifest_present && last.validation.manifest_parses} label="Manifest OK" />
                  <ConfigBadge ok={last.validation.collections_parsed_ok === last.validation.expected_collections} label={`JSON ${last.validation.collections_parsed_ok}/${last.validation.expected_collections}`} />
                  <ConfigBadge ok={last.validation.file_size_bytes >= last.validation.min_size_bytes} label={`Size ≥ ${formatBytes(last.validation.min_size_bytes)}`} />
                </div>
              </div>
            )}

            {last.drive && last.drive.latest_file_id && (
              <div className="mt-4" style={{ fontSize: '0.875rem' }}>
                <p className="cb-card-label">Google Drive</p>
                <p className="cb-num" style={{ fontSize: '0.8125rem' }}>
                  latest.zip → <code>{last.drive.latest_file_id}</code> ({last.drive.latest_action})<br />
                  {last.drive.daily_filename} → <code>{last.drive.daily_file_id}</code> ({last.drive.daily_action})
                </p>
                {last.pruned && last.pruned.length > 0 && (
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                    Pruned {last.pruned.length} older backup{last.pruned.length === 1 ? '' : 's'}.
                  </p>
                )}
              </div>
            )}

            {last.status === 'failed' && last.error_details && (
              <div className="mt-5" style={{ background: '#fdf4f4', border: '1px solid var(--cb-negative)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
                <p className="cb-card-label" style={{ color: 'var(--cb-negative)', marginBottom: 'var(--cb-space-2)' }}>Error Details</p>
                <pre data-testid="last-error" style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: '240px', color: 'var(--cb-ink)' }}>
                  {last.error_details}
                </pre>
              </div>
            )}
          </>
        )}
      </div>

      {/* ------- Recent history table ------- */}
      <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }} data-testid="history-card">
        <h2 className="cb-section-title" style={{ padding: 'var(--cb-space-4) var(--cb-space-5)', borderBottom: '1px solid var(--cb-line)', fontSize: '1rem' }}>
          Backup History
        </h2>
        <table data-testid="backup-history-table" className="cb-table" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th>Started (UTC)</th>
              <th>Status</th>
              <th>Source</th>
              <th>Triggered By</th>
              <th className="cb-num">Size</th>
              <th className="cb-num">Duration</th>
              <th className="cb-num">Docs</th>
              <th>Daily File</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center cb-muted" style={{ padding: 'var(--cb-space-5) 0' }}>No backups yet.</td>
              </tr>
            ) : (
              logs.map((b) => (
                <tr key={b.id} data-testid={`history-row-${b.id}`}>
                  <td className="cb-num" style={{ fontSize: '0.8125rem' }}>{formatDateTime(b.started_at)}</td>
                  <td><StatusPill status={b.status} /></td>
                  <td>{b.source}</td>
                  <td>{b.triggered_by}</td>
                  <td className="cb-num">{formatBytes(b.file_size_bytes)}</td>
                  <td className="cb-num">{formatDuration(b.duration_seconds)}</td>
                  <td className="cb-num">{b.total_documents?.toLocaleString() || 0}</td>
                  <td className="cb-muted" style={{ fontSize: '0.75rem' }}>{b.drive?.daily_filename || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ------- Migrations (admin-only, potentially destructive) ------- */}
      <MigrationsPanel />
    </div>
  );
};

export default Settings;
